import { useReducer, useEffect } from 'react';
import useApiClient from '../../modules/react/hooks/useApiClient';

const formatNavigationForMutation = ({ text, page, link, navigationItems }) => ({
  link: link && { href: link.href, target: link.target },
  pageId: page && page.id,
  navigationItems: navigationItems && navigationItems.map(formatNavigationForMutation),
  text,
});

const formatInputForMutation = (navigation) => {
  let primaryButton;
  let secondaryButton;

  const nav = navigation.map((navigationItem) => {
    const { isPrimary, isSecondary } = navigationItem;

    if (isPrimary) {
      primaryButton = formatNavigationForMutation(navigationItem);
      return null;
    }

    if (isSecondary) {
      secondaryButton = formatNavigationForMutation(navigationItem);
      return null;
    }

    return formatNavigationForMutation(navigationItem);
  }).filter(Boolean);

  return {
    primaryButton,
    secondaryButton,
    navigation: nav,
  };
};

const initialState = {
  dirty: false,
  mutation: {
    errors: undefined,
    submitted: false,
    submitting: false,
  },
  navigation: undefined,
  query: {
    error: undefined,
    loaded: false,
    loading: false,
  },
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'mutation/errors':
      return {
        ...state,
        mutation: {
          ...state.mutation,
          errors: action.errors,
        },
      };
    case 'mutation/submitted':
      return {
        ...state,
        dirty: false,
        mutation: {
          ...state.mutation,
          submitted: true,
          submitting: false,
        },
      };
    case 'mutation/submitting':
      return {
        ...state,
        mutation: {
          ...state.mutation,
          submitted: false,
          submitting: true,
        },
      };
    case 'navigation':
      return {
        ...state,
        dirty: true,
        navigation: action.navigation,
      };
    case 'query/error':
      return {
        ...state,
        query: {
          ...state.query,
          error: action.error,
        },
      };
    case 'query/loaded':
      return {
        ...state,
        dirty: false,
        query: {
          ...state.query,
          loaded: true,
          loading: false,
        },
      };
    case 'query/loading':
      return {
        ...state,
        query: {
          ...state.query,
          loading: true,
        },
      };
    case 'query/navigation':
      return {
        ...state,
        dirty: false,
        navigation: action.navigation,
      };
    case 'reset':
      return {
        ...initialState,
      };
    default:
      return state;
  }
};

const useEditableNavigation = (
  { query, options, dependencies, getNavigation },
  { mutation },
) => {
  const apiClient = useApiClient();
  const [state, dispatch] = useReducer(reducer, initialState);

  const queryOptions = typeof options === 'function'
    ? options(state)
    : options;

  const fetch = () => {
    apiClient.query({ query, ...queryOptions }).then((res) => {
      const navigation = typeof getNavigation === 'function'
        ? getNavigation(res.data)
        : res.data.navigation;
      dispatch({ type: 'query/navigation', navigation });
    }).catch((error) => {
      dispatch({ type: 'query/error', error });
    }).finally(() => {
      dispatch({ type: 'query/loaded' });
    });
  };

  const submit = (mutationOptions) => {
    apiClient.mutate({ mutation, ...mutationOptions }).then((res) => {
      if (res.data.userErrors && res.data.userErrors.length > 0) {
        dispatch({ type: 'mutation/errors', errors: res.data.userErrors });
        return;
      }

      dispatch({ type: 'mutation/submitted' });
    }).catch(() => {
      dispatch({ type: 'mutation/errors', errors: [{ code: 0, message: 'Could not update navigation.' }] });
    });
  };

  useEffect(() => {
    dispatch({ type: 'query/loading' });
    fetch();
  }, dependencies);

  const onChange = (navigation) => {
    dispatch({ type: 'navigation', navigation });
  };

  const onReset = ({ shouldFetch = false } = {}) => {
    dispatch({ type: 'reset' });

    if (shouldFetch) {
      fetch();
    }
  };

  const onSubmit = ({ variables = {}, ...rest } = {}) => {
    dispatch({ type: 'mutation/submitting' });
    submit({
      ...rest,
      variables: {
        ...variables,
        ...formatInputForMutation(state.navigation),
      },
    });
  };

  return [state, {
    onChange,
    onReset,
    onSubmit,
  }];
};

export default useEditableNavigation;
